/**
 * CBH — Article TOC (auto, world-class touches)
 * - Builds a TOC from H2/H3 headings inside [data-cbh-article-rte]
 * - Adds stable IDs if missing
 * - Smooth scroll + active section highlight
 * - Shows TOC only when there are enough headings
 *
 * World-class touches:
 * 1) Adds a "Back to top" item on longer pages
 * 2) Auto-open on desktop, collapsed by default on mobile (without fighting user toggles)
 * 3) Sets active item on load (hash + scroll position)
 */
(() => {
  'use strict';

  const MIN_HEADINGS = 3; // only show TOC when it adds value
  const LONG_PAGE_SCROLL_MULTIPLIER = 1.75; // "Back to top" when page is meaningfully scrollable
  const MOBILE_MQ = '(max-width: 749px)';
  const DEFAULT_HEADER_FALLBACK = 72; // px

  const slugify = (str) =>
    String(str || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const escapeHtml = (str) =>
    String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const uniqId = (base, used) => {
    let id = base;
    let i = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = `${base}-${i++}`;
    }
    used.add(id);
    return id;
  };

  const getHeaderOffset = () => {
    // Prefer a CSS var if you’re already setting one (common in your theme work)
    const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--cbh-header-bottom').trim();
    const parsed = parseInt(cssVar, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed;

    // Otherwise measure a reasonable header element
    const header =
      document.querySelector('#shopify-section-header') ||
      document.querySelector('.section-header') ||
      document.querySelector('header.header') ||
      document.querySelector('[data-section-type="header"]');

    if (header) {
      const r = header.getBoundingClientRect();
      const h = r.height || 0;
      // Use height as a stable-ish offset baseline
      if (h > 0) return Math.min(Math.round(h), 220);
    }

    return DEFAULT_HEADER_FALLBACK;
  };

  const ensureTopAnchor = (rte) => {
    if (!rte) return null;
    let top = document.getElementById('cbh-top');
    if (top) return top;

    // Create a lightweight anchor at the very top of the RTE
    top = document.createElement('span');
    top.id = 'cbh-top';
    top.setAttribute('aria-hidden', 'true');
    top.style.display = 'block';
    top.style.height = '1px';
    top.style.marginTop = '-1px';

    rte.prepend(top);
    return top;
  };

  const setDetailsDefaultOpenState = (tocWrap, details) => {
    if (!tocWrap || !details) return;

    // Don’t override if user already toggled in this session
    if (details.dataset.cbhUserToggled === '1') return;

    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    if (isMobile) details.removeAttribute('open');
    else details.setAttribute('open', '');
  };

  const initOne = (root = document) => {
    const tocWrap = root.querySelector('[data-cbh-article-toc]');
    const tocList = root.querySelector('[data-cbh-toc-list]');
    const rte = root.querySelector('[data-cbh-article-rte]');
    if (!tocWrap || !tocList || !rte) return;

    // Avoid double init
    if (tocWrap.dataset.cbhInit === '1') return;
    tocWrap.dataset.cbhInit = '1';

    const headings = Array.from(rte.querySelectorAll('h2, h3')).filter((h) => {
      return h.textContent && h.textContent.trim().length > 0;
    });

    if (headings.length < MIN_HEADINGS) return;

    const used = new Set();
    const items = [];

    const headerOffset = getHeaderOffset();
    const scrollMargin = `calc(${headerOffset}px + 24px)`;

    headings.forEach((h, idx) => {
      const level = h.tagName.toLowerCase(); // h2 / h3
      const text = h.textContent.trim();

      // Ensure the heading has an id
      if (!h.id) {
        const base = `cbh-${level}-${slugify(text) || 'section'}-${idx + 1}`;
        h.id = uniqId(base, used);
      } else {
        used.add(h.id);
      }

      // Better anchor landing vs sticky header
      h.style.scrollMarginTop = scrollMargin;

      items.push({ id: h.id, text, level });
    });

    // Optional "Back to top" (only when page is long enough)
    const isLongPage =
      document.documentElement.scrollHeight > window.innerHeight * LONG_PAGE_SCROLL_MULTIPLIER;

    if (isLongPage) {
      ensureTopAnchor(rte);
      items.push({ id: 'cbh-top', text: 'Back to top', level: 'top' });
    }

    // Build list markup (escape text to avoid any injection)
    tocList.innerHTML = items
      .map((it) => {
        const safeText = escapeHtml(it.text);
        const href = `#${encodeURIComponent(it.id)}`;

        let cls = 'cbh-toc__item';
        if (it.level === 'h3') cls += ' cbh-toc__item--h3';
        if (it.level === 'top') cls += ' cbh-toc__item--top';

        let linkCls = 'cbh-toc__link';
        if (it.level === 'top') linkCls += ' cbh-toc__link--top';

        return `<li class="${cls}">
          <a class="${linkCls}" href="${href}">${safeText}</a>
        </li>`;
      })
      .join('');

    tocWrap.hidden = false;

    const details = tocWrap.querySelector('details');
    const summary = tocWrap.querySelector('summary');

    // Default open/closed behaviour (desktop open, mobile collapsed)
    if (details) {
      // Track user toggles so we don’t fight them
      if (summary && !summary.dataset.cbhBound) {
        summary.dataset.cbhBound = '1';
        summary.addEventListener('click', () => {
          details.dataset.cbhUserToggled = '1';
        });
      }

      // Set initial state
      setDetailsDefaultOpenState(tocWrap, details);

      // Update on resize only if user hasn’t toggled
      const onResize = () => setDetailsDefaultOpenState(tocWrap, details);
      window.addEventListener('resize', onResize, { passive: true });
    }

    // Smooth scroll + URL update + close on mobile after click
    tocList.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;

      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Update URL hash without jumping (special-case top: clear hash)
      try {
        if (id === 'cbh-top') {
          history.replaceState(null, '', `${location.pathname}${location.search}`);
        } else {
          history.replaceState(null, '', `#${id}`);
        }
      } catch (_) {}

      // Set active immediately
      setActive(id);

      // Nice UX: close TOC on mobile after selecting
      const isMobile = window.matchMedia(MOBILE_MQ).matches;
      if (details && isMobile) details.removeAttribute('open');
    });

    // Active section highlight
    const links = Array.from(tocList.querySelectorAll('a[href^="#"]'));
    const byId = new Map(links.map((a) => [decodeURIComponent(a.getAttribute('href').slice(1)), a]));

    function setActive(id) {
      links.forEach((a) => a.removeAttribute('aria-current'));
      const a = byId.get(id);
      if (a) a.setAttribute('aria-current', 'true');
    }

    function setActiveFromHashOrScroll() {
      const hash = decodeURIComponent((location.hash || '').replace(/^#/, ''));
      if (hash && byId.has(hash)) {
        setActive(hash);
        return;
      }

      // Pick the last heading above the "reading line"
      const line = getHeaderOffset() + 32;
      let best = null;
      for (const h of headings) {
        const r = h.getBoundingClientRect();
        if (r.top <= line) best = h;
      }
      if (best && best.id) setActive(best.id);
    }

    // Set active on load immediately
    setActiveFromHashOrScroll();

    // Keep active updated (IntersectionObserver preferred)
    if ('IntersectionObserver' in window) {
      const topMargin = Math.min(200, getHeaderOffset() + 40);
      const obs = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((en) => en.isIntersecting)
            .sort((a, b) => (a.boundingClientRect.top || 0) - (b.boundingClientRect.top || 0));

          if (visible[0] && visible[0].target && visible[0].target.id) {
            setActive(visible[0].target.id);
          }
        },
        {
          root: null,
          rootMargin: `-${topMargin}px 0px -70% 0px`,
          threshold: 0.01
        }
      );

      headings.forEach((h) => obs.observe(h));
    } else {
      // Fallback: scroll listener
      let raf = 0;
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          setActiveFromHashOrScroll();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  };

  const init = () => initOne(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor / dynamic loads
  document.addEventListener('shopify:section:load', (e) => initOne(e.target));
})();
